/**
 * Date-only helpers — for operational date fields that must NEVER undergo
 * timezone conversion (batch start/end, booking date, enrollment start/end,
 * DOB, holidays, attendance, payroll month).
 *
 * Rule: NEVER use `new Date(stringFromServer)` on these fields. The default
 * JS Date constructor parses ISO strings without timezone information as
 * LOCAL time, which produces an off-by-one calendar day in any non-UTC
 * timezone and creates compounding drift on edit → save → re-open cycles.
 *
 * Use these helpers everywhere a `<input type="date">` reads or writes.
 */

/**
 * Parse any incoming value (string, datetime ISO, Date object) into a
 * pure `"YYYY-MM-DD"` calendar string — without `new Date()`.
 *
 * Accepted inputs:
 *   "2026-05-15"
 *   "2026-05-15T00:00:00"
 *   "2026-05-15T00:00:00Z"
 *   "2026-05-15T00:00:00+05:30"
 *   "2026-05-15T10:30:00.123Z"  → "2026-05-15" (UTC calendar day)
 *   Date object                 → uses UTC calendar day
 *   null / undefined / ""       → ""
 *
 * For datetimes with timezone info, we always pick the UTC calendar day so
 * the same instant decoded anywhere on the planet yields the same date —
 * matching the backend storage contract of UTC midnight.
 */
export function parseDateOnly(value) {
  if (!value) return "";
  if (typeof value === "string") {
    // Strict slice for the common YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss[.sss][Z|±HH:MM] patterns.
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
  }
  if (value instanceof Date && !isNaN(value)) {
    const y = value.getUTCFullYear();
    const mo = String(value.getUTCMonth() + 1).padStart(2, "0");
    const d = String(value.getUTCDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }
  return "";
}

/**
 * Serialise a `"YYYY-MM-DD"` form value for transmission to the backend.
 *
 * Returns the same string unchanged when valid; "" / null otherwise.
 *
 * We intentionally do NOT add a "T00:00:00Z" suffix. The backend expects
 * date-only strings for operational date fields. If a particular endpoint
 * still requires a datetime, the caller must opt in via `toUtcMidnightIso`.
 */
export function serializeDateOnly(value) {
  if (!value) return null;
  return parseDateOnly(value) || null;
}

/**
 * Escape hatch — for endpoints that still accept datetimes for date-only
 * fields (legacy contract). Returns `"YYYY-MM-DDT00:00:00.000Z"`.
 */
export function toUtcMidnightIso(value) {
  const s = parseDateOnly(value);
  return s ? `${s}T00:00:00.000Z` : null;
}

/**
 * Today as `"YYYY-MM-DD"` using the user's *local* calendar — for default
 * values on freshly opened forms. NEVER use this for serialisation of an
 * existing date value pulled from the server.
 */
export function todayDateOnly() {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

/**
 * Compare two `"YYYY-MM-DD"` strings safely. Returns negative / 0 / positive
 * (string comparison works correctly for ISO date-only format).
 */
export function compareDateOnly(a, b) {
  const sa = parseDateOnly(a);
  const sb = parseDateOnly(b);
  if (sa === sb) return 0;
  if (!sa) return -1;
  if (!sb) return 1;
  return sa < sb ? -1 : 1;
}
